import { ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useToast } from "~/hooks/use-toast";
import { cn } from "~/lib/utils";
import { useSkyViewStore } from "../../../store";

import { useSearchProjectPages } from "~/api/projects/projects";

type DocumentSearchPanelProps = {
  organizationId: string;
  projectId: string;
  documentId: string | undefined;
  onNavigateToPage: (pageId: string) => void;
};

type SearchResult = {
  pageNumber: number;
  pageId: string;
  score: number;
  snippet: string;
};

export const DocumentSearchPanel: FC<DocumentSearchPanelProps> = ({
  organizationId,
  projectId,
  documentId = undefined,
  onNavigateToPage,
}) => {
  const intl = useIntl();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [lastSearchedQuery, setLastSearchedQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const { mutateAsync: searchProjectPages, isPending } =
    useSearchProjectPages();
  const setSearchQuery = useSkyViewStore((state) => state.setSearchQuery);
  const setHighlightedPageId = useSkyViewStore(
    (state) => state.setHighlightedPageId
  );
  const clearSearchHighlight = useSkyViewStore(
    (state) => state.clearSearchHighlight
  );

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;

    const trimmedQuery = query.trim();
    try {
      const response = await searchProjectPages({
        organizationId,
        projectId,
        params: {
          documentId,
        },
        data: {
          query: trimmedQuery,
        },
      });
      setLastSearchedQuery(trimmedQuery);
      setResults(response.results);
      setCurrentResultIndex(0);
      setSearchQuery(trimmedQuery);
      if (response.results.length > 0) {
        onNavigateToPage(response.results[0].pageId);
        setHighlightedPageId(response.results[0].pageId);
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "docViewer.search.error",
          defaultMessage: "Search failed",
        }),
        description: intl.formatMessage({
          id: "docViewer.search.errorDescription",
          defaultMessage: "Unable to search the document. Please try again.",
        }),
      });
    }
  }, [
    query,
    organizationId,
    projectId,
    documentId,
    searchProjectPages,
    onNavigateToPage,
    toast,
    intl,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const trimmedQuery = query.trim();
      if (e.shiftKey && results.length > 0) {
        handlePrevious();
      } else if (results.length > 0 && trimmedQuery === lastSearchedQuery) {
        handleNext();
      } else {
        handleSearch();
      }
    } else if (e.key === "Escape") {
      handleClose();
    }
  };

  const handleNext = () => {
    if (results.length === 0) return;
    const nextIndex = (currentResultIndex + 1) % results.length;
    setCurrentResultIndex(nextIndex);
    onNavigateToPage(results[nextIndex].pageId);
    setHighlightedPageId(results[nextIndex].pageId);
  };

  const handlePrevious = () => {
    if (results.length === 0) return;
    const prevIndex =
      (currentResultIndex - 1 + results.length) % results.length;
    setCurrentResultIndex(prevIndex);
    onNavigateToPage(results[prevIndex].pageId);
    setHighlightedPageId(results[prevIndex].pageId);
  };

  const handleClose = () => {
    setIsOpen(false);
    setQuery("");
    setLastSearchedQuery("");
    setResults([]);
    setCurrentResultIndex(0);
    clearSearchHighlight();
  };

  const handleOpen = () => {
    setIsOpen(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  useEffect(() => {
    setQuery("");
    setLastSearchedQuery("");
    setResults([]);
    setCurrentResultIndex(0);
    clearSearchHighlight();
  }, [documentId, clearSearchHighlight]);

  useEffect(() => {
    const handleKeyboardShortcut = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "f") {
        e.preventDefault();
        if (isOpen) {
          inputRef.current?.focus();
          inputRef.current?.select();
        } else {
          handleOpen();
        }
      }
    };

    window.addEventListener("keydown", handleKeyboardShortcut);
    return () => window.removeEventListener("keydown", handleKeyboardShortcut);
  }, [isOpen]);

  if (!isOpen) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-md bg-card shadow-lg shadow-black/10"
            onClick={handleOpen}
          >
            <Search className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {intl.formatMessage({
              id: "docViewer.search.tooltip",
              defaultMessage: "Search in document",
            })}{" "}
            <kbd className="ml-1 rounded border border-border px-1 text-xs">
              {navigator.platform.includes("Mac") ? "⌘" : "Ctrl"}+F
            </kbd>
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div className="flex items-center gap-1 p-1 rounded-lg shadow-lg shadow-black/10 bg-card">
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={intl.formatMessage({
            id: "docViewer.search.placeholder",
            defaultMessage: "Search...",
          })}
          className="h-7 w-48 pr-8 text-xs"
          disabled={isPending}
        />
        {isPending && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
          </div>
        )}
      </div>

      {results.length > 0 && (
        <span className="text-xs text-muted-foreground whitespace-nowrap px-1">
          {currentResultIndex + 1}/{results.length}
        </span>
      )}

      <div className="flex items-center">
        <Button
          variant="ghost"
          size="icon"
          className={cn("h-6 w-6", results.length === 0 && "opacity-50")}
          onClick={handlePrevious}
          disabled={results.length === 0}
        >
          <ChevronUp className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className={cn("h-6 w-6", results.length === 0 && "opacity-50")}
          onClick={handleNext}
          disabled={results.length === 0}
        >
          <ChevronDown className="h-3 w-3" />
        </Button>
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        onClick={handleClose}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
};
